/** Briques d'interface partagées : carte, bouton, message, tableau, champ. */

import type { ButtonHTMLAttributes, ReactNode } from 'react'

import styles from './ui.module.css'

export function cx(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ')
}

// --- Carte -----------------------------------------------------------------

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cx(styles.card, className)}>{children}</section>
}

export function CardHead({ title, hint, actions }: { title: string; hint?: ReactNode; actions?: ReactNode }) {
  return (
    <header className={styles.cardHead}>
      <h3>{title}</h3>
      {hint ? <span className={styles.cardHint}>{hint}</span> : null}
      {actions ? <div style={{ marginLeft: hint ? 12 : 'auto' }}>{actions}</div> : null}
    </header>
  )
}

export function CardBody({ children, tight }: { children: ReactNode; tight?: boolean }) {
  return <div className={cx(styles.cardBody, tight && styles.tight)}>{children}</div>
}

export function CardNote({ children }: { children: ReactNode }) {
  return <p className={styles.cardNote}>{children}</p>
}

// --- Bouton ----------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'danger' | 'link'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  small?: boolean
  busy?: boolean
}

export function Button({ variant = 'primary', small, busy, children, className, ...rest }: ButtonProps) {
  const variantClass =
    variant === 'secondary'
      ? styles.secondary
      : variant === 'danger'
        ? styles.danger
        : variant === 'link'
          ? styles.link
          : undefined
  return (
    <button
      type="button"
      className={cx(variant === 'link' ? styles.link : styles.btn, variantClass, small && styles.sm, className)}
      disabled={rest.disabled || busy}
      {...rest}
    >
      {busy ? <span className={cx(styles.spinner, variant === 'primary' && styles.onDark)} aria-hidden /> : null}
      {children}
    </button>
  )
}

// --- Message ---------------------------------------------------------------

export function Message({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'ok' | 'info' | 'warn'
  children: ReactNode
}) {
  const tone = kind === 'ok' ? styles.ok : kind === 'info' ? styles.info : kind === 'warn' ? styles.warn : undefined
  return (
    <div className={cx(styles.message, tone)} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

// --- Mise en page -----------------------------------------------------------

export function Grid({
  columns = 2,
  children,
  className,
}: {
  columns?: 2 | 3 | 4
  children: ReactNode
  className?: string
}) {
  const size = columns === 4 ? styles.g4 : columns === 3 ? styles.g3 : styles.g2
  return <div className={cx(styles.grid, size, className)}>{children}</div>
}

export function SectionHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className={styles.sectionHead}>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
    </div>
  )
}

// --- Champ ------------------------------------------------------------------

export function Field({
  label,
  help,
  htmlFor,
  children,
}: {
  label: string
  help?: ReactNode
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor}>
        {label}
        {help ? <span className={styles.help}>{help}</span> : null}
      </label>
      {children}
    </div>
  )
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: ReactNode
  disabled?: boolean
}) {
  return (
    <label className={styles.check}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  )
}

// --- Divers -----------------------------------------------------------------

export function Spinner({ onDark }: { onDark?: boolean }) {
  return <span className={cx(styles.spinner, onDark && styles.onDark)} aria-hidden />
}

export function Tag({ tone, children }: { tone: 'ok' | 'warn'; children: ReactNode }) {
  return <span className={cx(styles.tag, tone === 'ok' ? styles.tagOk : styles.tagWarn)}>{children}</span>
}

export function Scroll({ children }: { children: ReactNode }) {
  return <div className={styles.scroll}>{children}</div>
}

export function Table({ children }: { children: ReactNode }) {
  return <table className={styles.table}>{children}</table>
}

export const numeric = styles.num
